import React, { useState } from "react";
import { TooltipArrow } from "../../../../components/ui";
import { formatDateTime } from "../../../../utils/datetime";
import { toggleTMReportSubmitted } from "../../../../services/api/tmlog";
import { getLatestInspector } from "../helpers";

/**
 * TMStatusIndicator Component
 * Displays status dots for T&M logs
 *
 * 4 status dots:
 * 1. Sent to Inspector - Gray (not sent) / Green (sent)
 * 2. Inspector Response - Gray (n/a) / Red (awaiting) / Yellow (signed w/ comments) / Green (approved)
 * 3. Priced - Gray (not priced) / Green (priced)
 * 4. Submitted - Gray (not submitted) / Green (submitted)
 */
const TMStatusIndicator = ({ log }) => {
  // Local state for optimistic updates on submitted status
  const [isSubmitted, setIsSubmitted] = useState(log.submitted || false);
  const [submittedAt, setSubmittedAt] = useState(log.submitted_at);

  // Extract data from log
  const priceUpdateHistory = log.pricing?.update_history || [];
  const isPriced = priceUpdateHistory.length > 0;

  // Get the most recent inspector (by sent_at)
  const latestInspector = getLatestInspector(log.inspectors);

  // Status color helper
  const getStatusColor = (stage) => {
    switch (stage) {
      case "complete":
        return "bg-green-500";
      case "in-progress":
        return "bg-yellow-500";
      case "issue":
        return "bg-red-500";
      default:
        return "bg-gray-300";
    }
  };

  // ==================== DOT 1: Sent to Inspector ====================
  const getSentStatus = () => {
    return latestInspector ? "complete" : "default";
  };

  const renderSentTooltipContent = () => {
    if (!latestInspector) {
      return "Not sent to inspector";
    }
    return `Sent to inspector: ${latestInspector.inspector_email}`;
  };

  // ==================== DOT 2: Inspector Response ====================
  const getResponseStatus = () => {
    if (!latestInspector) return "default"; // Gray - not sent
    if (!latestInspector.signed_at) return "default"; // Gray - awaiting signature
    if (latestInspector.inspector_comments) return "in-progress"; // Yellow - signed with comments
    return "complete"; // Green - signed and approved
  };

  const renderResponseTooltipContent = () => {
    if (!latestInspector) {
      return "No inspector assigned";
    }
    if (!latestInspector.signed_at) {
      return "Awaiting inspector signature";
    }
    const signedDate = formatDateTime(latestInspector.signed_at, "month DD, YYYY");
    if (latestInspector.inspector_comments) {
      return `Signed on ${signedDate} with comments`;
    }
    return `Signed and approved on ${signedDate}`;
  };

  // ==================== DOT 3: Priced ====================
  const renderPricingTooltipContent = () => {
    if (!isPriced) {
      return "Not priced yet";
    }

    // If there's only one update, show simple format
    if (priceUpdateHistory.length === 1) {
      return `Priced on ${priceUpdateHistory[0]}`;
    }

    // If multiple updates, show all of them
    return (
      <>
        <div className="font-semibold mb-1">Pricing History:</div>
        {priceUpdateHistory.map((timestamp, index) => (
          <div key={index} className="text-xs">
            {index + 1}. {timestamp}
          </div>
        ))}
      </>
    );
  };

  // ==================== DOT 4: Submitted ====================
  const handleToggleSubmitted = async (e) => {
    e.stopPropagation(); // Prevent event bubbling

    // Store previous state for rollback
    const previousSubmitted = isSubmitted;
    const previousSubmittedAt = submittedAt;

    // Optimistic update
    setIsSubmitted(!isSubmitted);
    setSubmittedAt(!isSubmitted ? new Date().toISOString() : null);

    try {
      await toggleTMReportSubmitted(log.id);
    } catch (error) {
      // Rollback on error
      setIsSubmitted(previousSubmitted);
      setSubmittedAt(previousSubmittedAt);
      console.error("Failed to toggle submitted status:", error);
    }
  };

  const renderSubmittedTooltipContent = () => {
    if (isSubmitted && submittedAt) {
      return `Submitted on ${formatDateTime(submittedAt, "month DD, YYYY")}`;
    }
    return "Not submitted yet";
  };

  return (
    <div className="flex gap-1">
      {/* Dot 1: Sent to Inspector */}
      <span
        className={`w-3 h-3 rounded-full ${getStatusColor(getSentStatus())} cursor-pointer relative group`}
      >
        <TooltipArrow
          text={renderSentTooltipContent()}
          position="above"
          className="text-white bg-gray-900 text-left whitespace-normal max-w-xs"
          usePortal
        />
      </span>

      {/* Dot 2: Inspector Response */}
      <span
        className={`w-3 h-3 rounded-full ${getStatusColor(getResponseStatus())} cursor-pointer relative group`}
      >
        <TooltipArrow
          text={renderResponseTooltipContent()}
          position="above"
          className="text-white bg-gray-900 text-left whitespace-normal max-w-xs"
          usePortal
        />
      </span>

      {/* Dot 3: Priced */}
      <span
        className={`w-3 h-3 rounded-full ${getStatusColor(isPriced ? "complete" : "default")} cursor-pointer relative group`}
      >
        <TooltipArrow
          text={renderPricingTooltipContent()}
          position="above"
          className="text-white bg-gray-900 text-left whitespace-normal max-w-xs"
          usePortal
        />
      </span>

      {/* Dot 4: Submitted */}
      <span
        className={`w-3 h-3 p rounded-full ${getStatusColor(isSubmitted ? "complete" : "default")} cursor-pointer relative group`}
      >
        <TooltipArrow
          text={renderSubmittedTooltipContent()}
          position="above"
          className="text-white bg-gray-900 text-left whitespace-normal max-w-xs"
          buttonText={isSubmitted ? "Mark as Not Submitted" : "Mark as Submitted"}
          onButtonClick={handleToggleSubmitted}
          buttonClassName={
            isSubmitted
              ? "bg-orange-600 hover:bg-orange-700 mb-1"
              : "bg-green-600 hover:bg-green-700 mb-1"
          }
          usePortal
        />
      </span>
    </div>
  );
};

export default TMStatusIndicator;
